import http from "../config/axios";
import { getFromStorage } from "../utils/storage";

let cachedSession = null;
let sessionPromise = null;
let cachedPublicConfig = null;
let publicConfigPromise = null;
let crispSdkPromise = null;

const isCrispScriptLoaded = () =>
  Boolean(document.querySelector('script[src*="client.crisp.chat/l.js"]'));

const withNickname = (session) => {
  if (!session) {
    return session;
  }
  if (session.nickname) {
    return session;
  }
  try {
    const user = JSON.parse(getFromStorage("USER_DATA") || "null");
    if (user?.username) {
      return { ...session, nickname: user.username };
    }
  } catch {
    // ignore invalid storage
  }
  return session;
};

export const fetchCrispSession = () => {
  if (cachedSession) {
    return Promise.resolve(cachedSession);
  }
  if (!sessionPromise) {
    sessionPromise = http
      .get("auth/crisp-ticket-signature")
      .then(({ data }) => {
        cachedSession = withNickname(data);
        return cachedSession;
      })
      .catch((err) => {
        sessionPromise = null;
        throw err;
      });
  }
  return sessionPromise;
};

export const fetchPublicChatConfig = () => {
  if (cachedPublicConfig) {
    return Promise.resolve(cachedPublicConfig);
  }
  if (!publicConfigPromise) {
    publicConfigPromise = http
      .get("crisp/chat-public")
      .then(({ data }) => {
        cachedPublicConfig = data;
        return data;
      })
      .catch((err) => {
        publicConfigPromise = null;
        throw err;
      });
  }
  return publicConfigPromise;
};

export const clearCrispSessionCache = () => {
  cachedSession = null;
  sessionPromise = null;
  cachedPublicConfig = null;
  publicConfigPromise = null;
  crispSdkPromise = null;
};

export const initializeCrispSdk = () => {
  if (crispSdkPromise) {
    return crispSdkPromise;
  }

  crispSdkPromise = fetchPublicChatConfig()
    .then(({ website_id: websiteId }) => {
      if (!websiteId) {
        throw new Error("Crisp website ID is not configured.");
      }

      window.$crisp = window.$crisp || [];
      window.CRISP_WEBSITE_ID = websiteId;

      if (isCrispScriptLoaded()) {
        window.$crisp.push(["do", "chat:hide"]);
        return websiteId;
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://client.crisp.chat/l.js";
        script.async = true;
        script.onload = () => {
          window.$crisp.push(["do", "chat:hide"]);
          resolve(websiteId);
        };
        script.onerror = () => reject(new Error("Failed to load Crisp chat SDK."));
        document.head.appendChild(script);
      });
    })
    .catch((error) => {
      crispSdkPromise = null;
      throw error;
    });

  return crispSdkPromise;
};

const buildTicketCenterParams = ({ email, hmac, nickname }) => {
  const params = new URLSearchParams({ email, hmac });
  if (nickname) {
    params.set("nickname", nickname);
  }
  return params;
};

export const buildTicketCenterSrc = (session) => {
  const { website_id: websiteId } = session;
  return `https://plugins.crisp.chat/urn:crisp.im:ticket-center:0/tickets/${websiteId}?${buildTicketCenterParams(session)}`;
};

export const buildTicketReplySrc = (session, ticketId) => {
  const { website_id: websiteId } = session;
  return `https://plugins.crisp.chat/urn:crisp.im:ticket-center:0/tickets/${websiteId}/reply/${ticketId}?${buildTicketCenterParams(session)}`;
};

export const openTicketCenterInNewTab = () =>
  fetchCrispSession().then((session) => {
    window.open(buildTicketCenterSrc(session), "_blank", "noopener,noreferrer");
  });
